import { useState, useEffect } from 'react'
import { FolderOpen, Plus, Edit, Trash2, X } from 'lucide-react'
import { albumsAPI } from '@/lib/api'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

export default function AlbumManagement({ open, onOpenChange, currentUser }) {
  const [albums, setAlbums] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Create/Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingAlbum, setEditingAlbum] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: true
  })

  // Fetch albums
  const fetchAlbums = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await albumsAPI.getAlbums()
      setAlbums(response.data.albums || [])
    } catch (error) {
      console.error('Error fetching albums:', error)
      setError('Failed to load albums')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      fetchAlbums()
    }
  }, [open])

  // Handle create album
  const handleCreateAlbum = () => {
    setEditingAlbum(null)
    setFormData({ name: '', description: '', is_public: true })
    setEditDialogOpen(true)
  }

  // Handle edit album
  const handleEditAlbum = (album) => {
    setEditingAlbum(album)
    setFormData({
      name: album.name,
      description: album.description || '',
      is_public: album.is_public === 1
    })
    setEditDialogOpen(true)
  }

  // Handle submit (create or edit)
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      setError('Album name is required')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      if (editingAlbum) {
        // Update existing album
        const response = await albumsAPI.updateAlbum(editingAlbum.id, {
          ...formData,
          is_public: formData.is_public ? 1 : 0
        })
        setAlbums(albums.map(a => a.id === editingAlbum.id ? response.data : a))
      } else {
        // Create new album
        const response = await albumsAPI.createAlbum({
          ...formData,
          is_public: formData.is_public ? 1 : 0
        })
        setAlbums([response.data, ...albums])
      }

      setEditDialogOpen(false)
      setFormData({ name: '', description: '', is_public: true })
    } catch (error) {
      console.error('Submit album error:', error)
      setError(error.response?.data?.error || `Failed to ${editingAlbum ? 'update' : 'create'} album`)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle delete album
  const handleDeleteAlbum = async (album) => {
    if (!confirm(`Are you sure you want to delete "${album.name}"?`)) return

    try {
      setError(null)
      await albumsAPI.deleteAlbum(album.id)
      setAlbums(albums.filter(a => a.id !== album.id))
    } catch (error) {
      console.error('Delete album error:', error)
      setError(error.response?.data?.error || 'Failed to delete album')
    }
  }

  const canEdit = (album) => {
    return currentUser && (currentUser.id === album.created_by || ['admin','superadmin'].includes(currentUser?.role))
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FolderOpen className="h-6 w-6" />
                  Album Management
                </DialogTitle>
                <DialogDescription>
                  Create and manage screenshot albums
                </DialogDescription>
              </div>
              <Button onClick={handleCreateAlbum}>
                <Plus className="h-4 w-4 mr-2" />
                New Album
              </Button>
            </div>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading albums...</p>
            </div>
          ) : albums.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <FolderOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No albums yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first album to organize screenshots
                </p>
                <Button onClick={handleCreateAlbum}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Album
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {albums.map((album) => (
                <Card key={album.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{album.name}</h3>
                          {album.is_public === 0 && (
                            <Badge variant="secondary" className="text-xs">Private</Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {album.screenshot_count || 0} {album.screenshot_count === 1 ? 'screenshot' : 'screenshots'}
                          </Badge>
                        </div>
                        {album.description && (
                          <p className="text-sm text-muted-foreground mb-2">{album.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Created by {album.creator_username}
                        </p>
                      </div>
                      {canEdit(album) && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAlbum(album)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteAlbum(album)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Album Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAlbum ? 'Edit Album' : 'Create New Album'}
            </DialogTitle>
            <DialogDescription>
              {editingAlbum ? 'Update album details' : 'Create a new album to organize your screenshots'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <Label htmlFor="album-name">Name *</Label>
                <Input
                  id="album-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter album name..."
                  required
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="album-description">Description</Label>
                <Textarea
                  id="album-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this album..."
                  rows={3}
                />
              </div>

              {/* Public/Private */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="album-public"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="album-public" className="cursor-pointer">
                  Make album public (visible to all users)
                </Label>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : (editingAlbum ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
